import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UserManager } from 'oidc-client-ts';
import { hasCodeInUrl } from 'oidc-react';
import { useLocation } from 'react-router-dom';
import { getOidcConfig, getPreLoginEmail } from '../../../selectors/auth';
import LoadingComponent from '../../LoadingComponent/LoadingComponent';
import AuthLayout from '../../AuthLayout/Old/AuthLayout';
import { Container, ContentBox, ErrorMessage } from './SSOCallbackPage.styles';
import { login } from '../../../api/sso';
import { authError, doPreLogout } from '../../../actions/authActions';
import { getLoginRoleInfo, syncStoreWithCurrentUserState } from '../../../util/loaderUtils';
import { logout } from '../../../api/user';
import { changeLoginStatus, updateUser } from '../../../actions';
import { sendTestTicketsToExpert } from '../../../api/tickets';
import { changeTab } from '../../../actions/creatorsSync';
import * as SessionAnalyticsService from '../../../services/SessionAnalyticsService/SessionAnalyticsService';
import { handleError } from '../../../services/ErrorService/ErrorService';
import * as AuthServiceModule from '@asurion-core/frontend-utils/lib/AuthService';
import AuthService from 'services/authService';
import { getExpertById } from '../../../api/expertQueries/expertQueriesGraphQL';
import environment from '../../../environment';
import { LOCAL } from '../../../constants/envTypes';
import { ACCOUNT_USER } from '../../../constants/roles';
import { UsernameMismatchError } from '../../../util';
// This is for unit tests mainly
let redirectTimer = null;

export function resetRedirect() {
  clearTimeout(redirectTimer);
}

function isEmailMismatch(preLoginEmail, ssoLoginUser) {
  if (!preLoginEmail || typeof preLoginEmail !== 'string') {
    return false;
  }

  const ssoUserEmail = ssoLoginUser?.profile?.email;

  if (!ssoUserEmail || typeof ssoUserEmail !== 'string') {
    return false;
  }

  return preLoginEmail.trim().toLowerCase() !== ssoUserEmail.trim().toLowerCase();
}

const SSOCallbackPage = ({ redirectDelay }) => {
  const location = useLocation();
  const oidcConfig = useSelector(getOidcConfig);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const [loaderLabel, setLoaderLabel] = useState('Signing you in...');
  const preLoginEmail = useSelector(getPreLoginEmail);

  function doDeferredPreLoginRedirect(delayInMs) {
    setLoaderLabel('Redirecting you back to login page...');

    redirectTimer = setTimeout(() => {
      window.location.href = '/pre-login';
    }, delayInMs);
  }

  const onSignIn = useCallback(
    async (user, clientId) => {
      const data = await login(user.id_token, clientId);

      const sessionData = data?.session;

      if (!sessionData || !sessionData.sessionToken) {
        throw new Error('Login attempt has failed. Please try again.');
      }

      if (sessionData?.userType === AuthServiceModule.USER_TYPE.PARTNER) {
        AuthService.saveSessionDataToStorage(sessionData);

        if (environment.app.currentEnv === LOCAL) {
          const redirectPageUrl = `${environment.businessLoginPage}?idToken=${user.id_token}&clientId=${clientId}`;
          console.log('Redirecting to business login page', redirectPageUrl);
          window.location.href = redirectPageUrl;

          return;
        }

        window.location.href = `${environment.businessLoginPage}`;
      } else {
        AuthService.saveSessionDataToStorage(sessionData);

        await syncStoreWithCurrentUserState(updateUser);

        // Account users who only have access to Logic Builder should be redirected there (SPD-12844)
        const isAccountUser = AuthService.current().roles?.includes(ACCOUNT_USER);
        if (isAccountUser) {
          window.location.href = '/logic-builder/';
          return;
        }

        const simplrExpertId = AuthService.current().simplrExpert?.id;
        const simplrExpert = simplrExpertId ? await getExpertById(simplrExpertId) : null;

        if (!simplrExpert) {
          showError();
          dispatch(authError("Couldn't sign in. Please try again or contact administrator."));
          handleError({ error: 'SSO Login Error: No Simplr Expert found for logged in user' });

          logout();

          return;
        }

        if (!simplrExpert.isActive) {
          logout();

          showError();
          dispatch(authError("Couldn't sign in. Account is not active. Please contact administrator."));
          handleError({ error: 'SSO Login Error: Simplr Expert is not active' });

          return;
        }

        dispatch(changeLoginStatus(true));

        const { isAdmin, isAdminOrAccountUser } = await getLoginRoleInfo();
        if (isAdminOrAccountUser) {
          dispatch(changeTab('all'));
        } else if (simplrExpert.expertType === 'NEW') {
          // In most cases, this will not be called because expert NEW status will be set to TRAINING on signup
          // but if anything goes wrong, this ensures that initial login leads to test tickets sent
          await sendTestTicketsToExpert(simplrExpert.id);
        }

        /** NOTE(apoorvyadav1|SPD-13362|2025-10-15):
         * Previously, FullStory was initialized when the website loaded, which is not ideal for privacy compliance.
         * Initialize FullStory if user is logged in and it's not already initialized
         */
        SessionAnalyticsService.initializeSessionAnalyticsServices();

        SessionAnalyticsService.identify(simplrExpert.id, {
          name: `${simplrExpert.firstName} ${simplrExpert.lastName}`,
          email: simplrExpert.email,
          specialist: isAdmin,
        });

        SessionAnalyticsService.event('User Logged In');

        window.location.href = '/home';
      }
    },
    [dispatch]
  );

  const errorHandledRef = useRef(false);

  function showError(message = 'Authentication failed') {
    setError(message);
    errorHandledRef.current = true;
  }

  useEffect(() => {
    (async () => {
      if (!oidcConfig) {
        if (!errorHandledRef.current) {
          showError();
          dispatch(authError("Couldn't sign in. Please try again or contact administrator."));
          handleError({ error: 'No OIDC config found' });
          doDeferredPreLoginRedirect(redirectDelay);
        }

        return;
      }

      const userManager = new UserManager({
        ...oidcConfig,
        ...(environment.auth?.ssoRedirectUri && { redirect_uri: environment.auth.ssoRedirectUri }),
      });

      try {
        const user = await userManager.signinCallback();

        if (isEmailMismatch(preLoginEmail, user)) {
          throw new UsernameMismatchError(
            `Authentication failed. The email entered on Simplr (${preLoginEmail}) doesn’t match the one entered on the SSO login page (${user.profile.email}). Please use the same email.`,
            preLoginEmail,
            user.profile.email
          );
        }

        if (hasCodeInUrl(location)) {
          await onSignIn(user, oidcConfig.client_id);
        }
      } catch (_error) {
        userManager.removeUser();
        handleError({ error: _error });

        let errorMessage = "Couldn't sign in. Please try again or contact administrator.";

        if (_error instanceof UsernameMismatchError) {
          errorMessage = _error.message;
        }

        showError();
        dispatch(authError(errorMessage));
        dispatch(doPreLogout());
        doDeferredPreLoginRedirect(redirectDelay);
      }
    })();
  }, [redirectDelay, onSignIn, dispatch, location, preLoginEmail, error, oidcConfig]);

  return (
    <div>
      <AuthLayout>
        <Container>
          <ContentBox>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            <div>
              <LoadingComponent delay={0} marginTop={'0px'} label={loaderLabel} />
            </div>
          </ContentBox>
        </Container>
      </AuthLayout>
    </div>
  );
};

export default SSOCallbackPage;
